import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { PlatformBadge } from "@/components/ui/PlatformBadge/PlatformBadge";
import { StatusPill } from "@/components/ui/StatusPill/StatusPill";
import { HeroPreview } from "@/components/marketing/HeroPreview";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingSectionHeader } from "@/components/marketing/MarketingSectionHeader";
import { PLANS } from "@notamen/core/billing";
import type { Plan } from "@notamen/core/billing";
import styles from "./Marketing.module.css";

const PLAN_ORDER: Array<keyof typeof PLANS> = ["solo", "studio", "agency", "enterprise"];

function priceLabel(plan: Plan): string {
  return plan.priceMonthly === null ? "Custom" : `$${plan.priceMonthly}`;
}

const BRAND_KINDS = [
  {
    kind: "Ecommerce",
    tagline: "Catalog-aware drafts.",
    description:
      "Connects to Shopify or WooCommerce. Synced products become the source for product-in-use posts and per-product photo galleries you fill with your own shots.",
    highlights: ["Shopify + WooCommerce sync", "Per-product photo galleries", "UTM-tagged product links"],
  },
  {
    kind: "Service",
    tagline: "Outcomes, not units.",
    description:
      "No catalog. The brain knows you don't sell products, so it drafts case studies, process explainers, and framework breakdowns instead of inventing a fake SKU.",
    highlights: ["Case-study posts", "Process explainers", "Framework breakdowns"],
  },
  {
    kind: "Creator",
    tagline: "Your voice, your camera.",
    description:
      "Content-led, no products. Recurring series, perspective posts, and community spotlights — drafts that read like a person, not a brand.",
    highlights: ["Recurring series cadence", "Perspective posts", "Community spotlights"],
  },
  {
    kind: "Media",
    tagline: "Article-aware promo.",
    description:
      "Publication or podcast brain. Article promos, evergreen resurfacing, quote cards, and news reactions — all without losing your editorial voice.",
    highlights: ["Article promo carousels", "Quote-card generation", "News-react templates"],
  },
];

const ROADMAP = [
  "One-tap approve / edit / reject inside Slack",
  "Per-brand approval channels with per-role permissions",
  "Daily draft digest posted to your channel",
];

export function Marketing() {
  return (
    <>
      <MarketingNav />

      <main className={styles.main}>
        {/* — Hero — */}
        <section className={styles.hero}>
          <div className={styles.heroGlow} aria-hidden="true" />
          <div className={styles.heroInner}>
            <div className={styles.heroCopy}>
              <span className={styles.eyebrow}>
                <span className={styles.eyebrowDot} aria-hidden="true" />
                Built on Claude · Anthropic + AWS Bedrock
              </span>

              <h1 className={styles.headline}>
                Learns your brand once.{" "}
                <span className={styles.headlineAccent}>Drafts in your voice</span> every
                day.
              </h1>

              <p className={styles.heroTagline}>Leave your mark.</p>

              <p className={styles.subhead}>
                One brand brain trained on how <em>you</em> sound. Variants across
                Instagram, Facebook, and TikTok, each checked against your hard rules
                before it reaches you. Approve from the queue on a laptop — or from the
                swipe deck on your phone.
              </p>

              <div className={styles.heroCtas}>
                <Btn href="/signup" variant="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
                  Join the waitlist
                </Btn>
                <Btn href="#how-it-works" variant="secondary" size="lg" iconLeft={<Icon name="zap" size={16} />}>
                  See how it works
                </Btn>
              </div>

              <div className={styles.heroProof}>
                <Icon name="check-circle" size={15} />
                <span>
                  4 brand kinds · 3 platforms · Shopify + WooCommerce sync ·
                  brand-brain driven
                </span>
              </div>
              <p className={styles.heroNote}>
                Private beta. Onboarding waves are small and hands-on — no card up front.
              </p>
            </div>

            <HeroPreview />
          </div>
        </section>

        {/* — Honest capability strip — */}
        <section className={styles.strip}>
          <div className={styles.stripInner}>
            <span className={styles.stripLabel}>What it does today</span>
            {[
              "Brand-brain training",
              "Daily AI drafts",
              "Validator safety rails",
              "Approval queue",
              "Calendar + scheduling",
              "Shopify / Woo sync",
            ].map((item) => (
              <span key={item} className={styles.stripItem}>
                <Icon name="check" size={13} color="var(--accent)" strokeWidth={3} />
                {item}
              </span>
            ))}
          </div>
        </section>

        {/* — How it works — */}
        <section id="how-it-works" className={styles.section}>
          <div className={styles.sectionInner}>
            <MarketingSectionHeader
              kicker="How it works"
              title="One brain. Drafts a day. Approved in seconds."
              caption="No prompt engineering and no content calendar to maintain by hand. The system learns your voice once, then runs every day — and every draft is checked against your hard rules before you ever see it."
            />

            <div className={styles.steps}>
              <article className={styles.step}>
                <div className={styles.stepPreview}>
                  <pre className={styles.brainCode}>
                    <code>
                      <span className={styles.k}>"name"</span>:{" "}
                      <span className={styles.s}>"FestivalParent"</span>,{"\n"}
                      <span className={styles.k}>"kind"</span>:{" "}
                      <span className={styles.s}>"ecommerce"</span>,{"\n"}
                      <span className={styles.k}>"tone"</span>: {"{"}
                      {"\n"}
                      {"  "}
                      <span className={styles.k}>"voice"</span>: [
                      <span className={styles.s}>"warm"</span>,{" "}
                      <span className={styles.s}>"matter-of-fact"</span>],{"\n"}
                      {"  "}
                      <span className={styles.k}>"avoid"</span>: [
                      <span className={styles.s}>"vibes"</span>,{" "}
                      <span className={styles.s}>"lit"</span>]{"\n"}
                      {"}"}
                      {"\n"}
                      <span className={styles.c}>{"// + audience, lexicon,"}</span>
                      {"\n"}
                      <span className={styles.c}>{"// products, hard rules"}</span>
                    </code>
                  </pre>
                </div>
                <span className={styles.stepNumber}>STEP 01</span>
                <h3 className={styles.stepTitle}>Define your brand brain.</h3>
                <p className={styles.stepBody}>
                  Audience, voice, lexicon, products, hard rules. About fifteen minutes,
                  editable forever. The brain is a single record we version on every
                  change.
                </p>
              </article>

              <article className={styles.step}>
                <div className={styles.stepPreview}>
                  <div className={styles.draftList}>
                    {[
                      { p: "ig" as const, k: "Carousel · 5 slides", h: "Every crew has one. The patch-pocket friend…" },
                      { p: "tt" as const, k: "Reel · 0:14", h: "POV: you're the friend with the spare battery." },
                      { p: "fb" as const, k: "Image · caption", h: "Day-three kit — 5 essentials we never travel without." },
                    ].map((d, i) => (
                      <div key={i} className={styles.draftRow}>
                        <PlatformBadge platform={d.p} size={16} />
                        <div className={styles.draftText}>
                          <span className={styles.draftHook}>{d.h}</span>
                          <span className={styles.draftKind}>{d.k}</span>
                        </div>
                        <StatusPill state="pending" size="sm" withDot={false} />
                      </div>
                    ))}
                  </div>
                </div>
                <span className={styles.stepNumber}>STEP 02</span>
                <h3 className={styles.stepTitle}>Get drafts in your voice.</h3>
                <p className={styles.stepBody}>
                  The AI writes hooks, bodies, hashtags, and visual briefs across IG, FB,
                  and TikTok. A validator that knows your hard rules flags problems before
                  a draft ever reaches you.
                </p>
              </article>

              <article className={styles.step}>
                <div className={styles.stepPreview}>
                  <div className={styles.approve}>
                    <div className={styles.approveRow}>
                      <PlatformBadge platform="ig" size={16} />
                      <span className={styles.approveTitle}>Carousel · pending review</span>
                    </div>
                    <div className={styles.approveBtns}>
                      <span className={`${styles.miniBtn} ${styles.miniPrimary}`}>
                        <Icon name="check" size={12} strokeWidth={3} />
                        Approve
                      </span>
                      <span className={styles.miniBtn}>Edit</span>
                      <span className={styles.miniBtn}>Reject</span>
                    </div>
                    <div className={styles.approveSched}>
                      <Icon name="calendar" size={12} />
                      Scheduled · auto-publishes at post time
                    </div>
                  </div>
                </div>
                <span className={styles.stepNumber}>STEP 03</span>
                <h3 className={styles.stepTitle}>Approve from anywhere.</h3>
                <p className={styles.stepBody}>
                  In the web app or the mobile PWA swipe deck. Approve, edit, regenerate,
                  or reject. Approved drafts schedule themselves and publish to your
                  connected platforms.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* — Brand kinds — */}
        <section id="brand-kinds" className={`${styles.section} ${styles.sectionAlt}`}>
          <div className={styles.sectionInner}>
            <MarketingSectionHeader
              kicker="Built for every brand kind"
              title="Not an ecommerce-only tool."
              caption="The brand brain adapts to what you actually sell. A creator's brain doesn't waste tokens on a product catalog; a service brand's brain never invents a fake SKU."
            />

            <div className={styles.kinds}>
              {BRAND_KINDS.map((k) => (
                <article className={styles.kind} key={k.kind}>
                  <div className={styles.kindHead}>
                    <span className={styles.kindBadge}>{k.kind}</span>
                    <span className={styles.kindTagline}>{k.tagline}</span>
                  </div>
                  <p className={styles.kindBody}>{k.description}</p>
                  <ul className={styles.kindBullets}>
                    {k.highlights.map((h) => (
                      <li key={h}>
                        <Icon name="check" size={13} color="var(--accent)" strokeWidth={3} />
                        {h}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* — Roadmap (Slack) — */}
        <section id="slack" className={styles.section}>
          <div className={`${styles.sectionInner} ${styles.split}`}>
            <div className={styles.splitCopy}>
              <span className={styles.kicker}>On the roadmap</span>
              <h2 className={styles.splitTitle}>
                Slack approvals are coming next.
              </h2>
              <p className={styles.splitBody}>
                Today you approve drafts in the web app and the mobile PWA. A Slack app —
                so you can approve where your team already works — is on the roadmap, not
                shipped yet. Here's what we're building toward:
              </p>
              <ul className={styles.roadmapList}>
                {ROADMAP.map((r) => (
                  <li key={r}>
                    <span className={styles.roadmapTick}>
                      <Icon name="check" size={11} strokeWidth={3} />
                    </span>
                    <span>
                      {r} <span className={styles.roadmapTag}>(on the roadmap)</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.roadmapCard}>
              <div className={styles.roadmapCardHead}>
                <Icon name="slack" size={16} color="oklch(0.50 0.16 290)" />
                <span className={styles.roadmapCardTitle}>Slack app</span>
                <span className={styles.roadmapPill}>Coming soon</span>
              </div>
              <p className={styles.roadmapCardBody}>
                Until the Slack app ships, every approval lives in the in-app queue —
                fully functional today, with the same approve / edit / regenerate /
                reject actions you'll get in Slack later.
              </p>
              <Btn href="/changelog" variant="secondary" size="md" iconRight={<Icon name="arrow-up-right" size={15} />}>
                Follow the changelog
              </Btn>
            </div>
          </div>
        </section>

        {/* — Pricing — */}
        <section className={`${styles.section} ${styles.sectionAlt}`}>
          <div className={styles.sectionInner}>
            <MarketingSectionHeader
              kicker="Pricing"
              title="Pick by how many brands you run."
              caption="Four plans, priced by brands and drafts. Pricing kicks in only when you're invited off the waitlist — no card collected up front."
            />

            <div className={styles.pricing}>
              {PLAN_ORDER.map((id) => {
                const plan = PLANS[id];
                const isEnterprise = plan.id === "enterprise";
                return (
                  <article
                    key={plan.id}
                    className={`${styles.plan} ${plan.recommended ? styles.planFeatured : ""}`}
                  >
                    {plan.recommended && <span className={styles.planBadge}>Most popular</span>}
                    <div className={styles.planTop}>
                      <span className={styles.planName}>{plan.name}</span>
                      <span className={styles.planBlurb}>{plan.blurb}</span>
                    </div>
                    <div className={styles.planPriceRow}>
                      <span className={styles.planPrice}>{priceLabel(plan)}</span>
                      <span className={styles.planCadence}>
                        {isEnterprise ? "" : plan.cadence}
                      </span>
                    </div>
                    <Btn
                      href={isEnterprise ? "mailto:sales@socialmediamanager.pheonixapps.com" : "/signup"}
                      variant={plan.recommended ? "primary" : "secondary"}
                      size="lg"
                      full
                    >
                      {isEnterprise ? "Talk to sales" : "Join the waitlist"}
                    </Btn>
                    <div className={styles.planDivider} />
                    <ul className={styles.planFeatures}>
                      {plan.features.map((f) => {
                        const slack = /slack/i.test(f);
                        return (
                          <li key={f}>
                            <Icon name="check" size={13} color="var(--accent)" strokeWidth={3} />
                            <span>
                              {f}
                              {slack && <span className={styles.roadmapTag}> (on the roadmap)</span>}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </article>
                );
              })}
            </div>

            <div className={styles.pricingFoot}>
              <span>
                All four tiers above are the real plans — Solo ${PLANS.solo.priceMonthly}, Studio $
                {PLANS.studio.priceMonthly}, Agency ${PLANS.agency.priceMonthly}, Enterprise custom.
              </span>
              <Btn href="/pricing" variant="ghost" size="md" iconRight={<Icon name="arrow-right" size={15} />}>
                See all plans
              </Btn>
            </div>
          </div>
        </section>

        {/* — Final CTA — */}
        <section className={styles.ctaSection}>
          <div className={styles.ctaCard}>
            <div className={styles.ctaGlow} aria-hidden="true" />
            <div className={styles.ctaInner}>
              <h2 className={styles.ctaTitle}>Stop staring at the blank caption box.</h2>
              <p className={styles.ctaBody}>
                Join the waitlist and we'll bring you in on the next onboarding wave.
                Small batches, hands-on setup, no card up front.
              </p>
              <div className={styles.ctaBtns}>
                <Btn href="/signup" variant="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
                  Join the waitlist
                </Btn>
                <Btn href="/pricing" variant="ghost" size="lg">
                  See pricing
                </Btn>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}
